"use client";

import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '@/utils/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Mail, User, Building, CheckCircle, Loader2 } from 'lucide-react';
import { showSuccess, showError } from '@/utils/toast';

const Onboarding = () => {
  const { user, profile, updateProfile } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'welcome' | 'redeem' | 'complete'>('welcome');
  const [inviteCode, setInviteCode] = useState(searchParams.get('invite_code') || '');
  const [companyName, setCompanyName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<'client' | 'agent' | 'admin'>('client');

  // Check for invite code on mount
  useEffect(() => {
    const code = searchParams.get('invite_code');
    const roleParam = searchParams.get('role');
    if (code) {
      setInviteCode(code);
      setRole(roleParam as 'client' | 'agent' | 'admin' || 'client');
      setStep('redeem');
    }
  }, [searchParams]);

  // Redirect if already onboarded
  useEffect(() => {
    if (profile?.onboarding_completed) {
      navigate('/dashboard');
    }
  }, [profile, navigate]);

  const redeemInvite = async () => {
    if (!inviteCode) {
      showError('Invite code is required');
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('redeem-invite', {
        body: { 
          invite_code: inviteCode, 
          user_id: user?.id, 
          first_name: firstName, 
          last_name: lastName,
          role 
        },
      });

      if (error) {
        showError(error.message || 'Failed to redeem invite');
      } else if (data?.success) {
        const company_name = data?.company_name || '';
        const returnedCompanyId = data?.company_id;

        setCompanyName(company_name);
        setStep('complete');
        showSuccess('Welcome to the team!');
        
        if (user?.id) {
          // Prefer company_id from response if available
          await updateProfile({
            ...(returnedCompanyId ? { company_id: returnedCompanyId } : {}),
            onboarding_completed: true,
          });
        }
      } else {
        showError(data?.error || 'Failed to redeem invite');
      }
    } catch (error: any) {
      console.error('Redeem invite error:', error);
      showError('Failed to redeem invite');
    } finally {
      setLoading(false);
    }
  };

  const completeOnboarding = async () => {
    if (!firstName || !lastName) {
      showError('Name is required');
      return;
    }

    setLoading(true);
    try {
      // Update profile
      if (user?.id) {
        const { error } = await supabase
          .from('profiles')
          .update({
            first_name: firstName,
            last_name: lastName,
            onboarding_completed: true,
          })
          .eq('id', user.id);

        if (error) throw error;
      }

      showSuccess('Onboarding complete!');
      navigate('/dashboard');
    } catch (error: any) {
      console.error('Complete onboarding error:', error);
      showError('Failed to complete onboarding');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Processing...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl text-center">Welcome to WightWay Connect!</CardTitle>
          <CardDescription className="text-center">
            Let's get you set up {step === 'complete' ? 'and ready to go!' : 'in your team.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {step === 'welcome' && (
            <div className="text-center">
              <p className="text-muted-foreground mb-4">
                Get started by creating your first company or join an existing one with an invite.
              </p>
              <div className="space-y-3">
                <Button asChild className="w-full">
                  <Link to="/signup?role=admin">Create Company Account</Link>
                </Button>
                <Button variant="outline" className="w-full">
                  Join with Invite Code
                </Button>
              </div>
            </div>
          )}

          {step === 'redeem' && (
            <div className="space-y-4">
              <div className="flex items-center space-x-2 mb-4">
                <Mail className="h-5 w-5 text-primary" />
                <span className="font-medium">Join {role} Team</span>
                <Badge variant="secondary">{role}</Badge>
              </div>
              <div className="space-y-2">
                <Label htmlFor="inviteCode">Invite Code</Label>
                <Input
                  id="inviteCode"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value)}
                  placeholder="Enter invite code"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="First name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Last name"
                  />
                </div>
              </div>
              <Button onClick={redeemInvite} className="w-full" disabled={!inviteCode || loading}>
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle className="mr-2 h-4 w-4" />}
                Redeem Invite
              </Button>
              <div className="text-center text-sm text-muted-foreground">
                Don't have an invite? <Link to="/signup?role=admin" className="text-primary hover:underline">Create your company</Link>
              </div>
            </div>
          )}

          {step === 'complete' && (
            <div className="text-center space-y-4">
              <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
              <h3 className="text-xl font-semibold">Welcome to {companyName}!</h3>
              <p className="text-muted-foreground">
                Your account has been created and you're ready to start working.
              </p>
              <Button onClick={completeOnboarding} className="w-full">
                Complete Setup & Go to Dashboard
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Onboarding;